import React from "react";
import {
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { tokens } from "../../theme";
import { legalContact } from "../../pages/legalContact";

// The AppBar is `position: sticky` and roughly 70px tall (45px logo + py: 1.5 + 1px border).
// Anchored headings need to clear it, and the sticky table of contents needs to sit below
// it, so both offsets come from here.
export const HEADER_OFFSET_PX = 88;
export const SECTION_SCROLL_MARGIN = { xs: "80px", md: `${HEADER_OFFSET_PX}px` };

/**
 * Accent colour for these pages. In light mode `primary.main` is #1e88e5, which only
 * reaches ~3.5:1 against the page background — acceptable for icons and rules, but below
 * AA for the inline links that these documents are full of. `primary.dark` (#1565c0) gets
 * to 5.4:1. In dark mode the roles are reversed, so `primary.main` (4.7:1) is the readable
 * one. Scoped here rather than changed in theme.js so the rest of the site is untouched.
 */
export const legalAccent = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.primary.main : theme.palette.primary.dark;

// Units are explicit strings on purpose: in MUI's `sx`, a bare `width: 1` is the shorthand
// for 100%, which turns this into a full-width absolutely-positioned box and gives every
// page a horizontal scrollbar.
const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
  border: 0,
};

/**
 * Renders a block's `text` field, which is either a plain string (the common case) or an
 * array of fragments for the few paragraphs that contain links. A fragment is either a
 * string or an object:
 *
 *   { text, to }                  -> in-app react-router link
 *   { text, href }                -> plain anchor (mailto:, tel:)
 *   { text, href, external: true } -> new tab, with the usual rel guard
 *   { text, bold: true }          -> <strong>
 *
 * Deliberately not markdown: the site has no markdown renderer and this avoids both a new
 * dependency and dangerouslySetInnerHTML.
 */
export const renderInline = (value) => {
  if (!Array.isArray(value)) {
    return value;
  }

  return value.map((fragment, index) => {
    if (typeof fragment === "string") {
      return <React.Fragment key={index}>{fragment}</React.Fragment>;
    }

    if (fragment.bold) {
      return (
        <Box component="strong" key={index} sx={{ fontWeight: 600 }}>
          {fragment.text}
        </Box>
      );
    }

    if (fragment.to) {
      return (
        <Link
          key={index}
          component={RouterLink}
          to={fragment.to}
          onClick={() => window.scrollTo(0, 0)}
          underline="hover"
          sx={(theme) => ({ fontWeight: 600, color: legalAccent(theme) })}
        >
          {fragment.text}
        </Link>
      );
    }

    const externalProps = fragment.external
      ? { target: "_blank", rel: "noopener noreferrer" }
      : {};

    return (
      <Link
        key={index}
        href={fragment.href}
        underline="hover"
        sx={(theme) => ({ fontWeight: 600, color: legalAccent(theme) })}
        {...externalProps}
      >
        {fragment.text}
        {fragment.external ? (
          <Box component="span" sx={visuallyHidden}>
            {" "}
            (opens in a new tab)
          </Box>
        ) : null}
      </Link>
    );
  });
};

const LegalParagraph = ({ block, component = "p" }) => {
  const theme = useTheme();

  return (
    <Typography
      component={component}
      variant="body1"
      sx={{
        fontSize: "1.0625rem",
        lineHeight: 1.8,
        color: theme.palette.text.secondary,
        m: 0,
      }}
    >
      {renderInline(block.text)}
    </Typography>
  );
};

const LegalList = ({ block }) => {
  const theme = useTheme();

  return (
    <Box
      component="ul"
      sx={{
        pl: 3,
        m: 0,
        "& li": { mb: 1 },
        "& li:last-of-type": { mb: 0 },
        "& li::marker": { color: legalAccent(theme) },
      }}
    >
      {block.items.map((item, index) => (
        <LegalParagraph key={index} component="li" block={{ text: item }} />
      ))}
    </Box>
  );
};

const LegalAddress = ({ block }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const contact = block.contact || legalContact;

  return (
    <Paper
      elevation={0}
      component="address"
      sx={{
        backgroundColor: colors.background[300],
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 3,
        fontStyle: "normal",
        maxWidth: 420,
      }}
    >
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 1 }}>
        {contact.name}
      </Typography>
      <Stack spacing={0.25}>
        {contact.lines.map((line, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
          >
            {line}
          </Typography>
        ))}
      </Stack>
      {contact.email ? (
        <Typography variant="body2" sx={{ mt: 1.5, color: theme.palette.text.secondary }}>
          Email:{" "}
          <Link
            href={`mailto:${contact.email}`}
            underline="hover"
            sx={{ fontWeight: 600, color: legalAccent(theme) }}
          >
            {contact.email}
          </Link>
        </Typography>
      ) : null}
    </Paper>
  );
};

const LegalTable = ({ block }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        backgroundColor: colors.background[300],
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        // Long tables scroll inside their own container so the page body never scrolls
        // horizontally on a phone.
        overflowX: "auto",
      }}
    >
      <Table size="small" sx={{ minWidth: 560 }}>
        {block.caption ? (
          <Box component="caption" sx={visuallyHidden}>
            {block.caption}
          </Box>
        ) : null}
        <TableHead>
          <TableRow>
            {block.columns.map((column) => (
              <TableCell
                key={column}
                scope="col"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  whiteSpace: "nowrap",
                }}
              >
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {block.rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell
                  key={cellIndex}
                  sx={{
                    color: theme.palette.text.secondary,
                    borderBottom:
                      rowIndex === block.rows.length - 1
                        ? "none"
                        : `1px solid ${theme.palette.divider}`,
                    verticalAlign: "top",
                    lineHeight: 1.7,
                  }}
                >
                  {renderInline(cell)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const LegalCallout = ({ block }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        borderLeft: `4px solid ${legalAccent(theme)}`,
        backgroundColor: colors.background[300],
        borderRadius: 1,
        px: { xs: 2.5, md: 4 },
        py: 3,
      }}
    >
      {block.title ? (
        <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 1.5 }}>
          {block.title}
        </Typography>
      ) : null}
      <Stack spacing={2}>
        {block.blocks.map((child, index) => (
          <LegalBlock key={index} block={child} />
        ))}
      </Stack>
    </Box>
  );
};

const LegalSubsection = ({ block }) => {
  const theme = useTheme();

  return (
    <Box>
      <Typography
        variant="h3"
        sx={{
          fontSize: { xs: "1.05rem", md: "1.15rem" },
          color: theme.palette.text.primary,
          mb: 1.5,
        }}
      >
        {block.title}
      </Typography>
      <Stack spacing={2}>
        {block.blocks.map((child, index) => (
          <LegalBlock key={index} block={child} />
        ))}
      </Stack>
    </Box>
  );
};

export const LegalBlock = ({ block }) => {
  switch (block.type) {
    case "paragraph":
      return <LegalParagraph block={block} />;
    case "list":
      return <LegalList block={block} />;
    case "address":
      return <LegalAddress block={block} />;
    case "table":
      return <LegalTable block={block} />;
    case "callout":
      return <LegalCallout block={block} />;
    case "subsection":
      return <LegalSubsection block={block} />;
    default:
      return null;
  }
};

/**
 * One numbered section of a legal document. The number comes from the caller's array index
 * rather than the data, so the heading, the table of contents entry and the anchor can
 * never drift out of step.
 */
export const LegalSection = ({ section, number }) => {
  const theme = useTheme();

  return (
    <Box component="section" aria-labelledby={`${section.id}-heading`} sx={{ mb: { xs: 5, md: 6 } }}>
      <Typography
        id={section.id}
        variant="h2"
        sx={{
          // Load-bearing: without this an anchor jump parks the heading underneath the
          // sticky AppBar.
          scrollMarginTop: SECTION_SCROLL_MARGIN,
          fontSize: { xs: "1.375rem", md: "1.75rem" },
          color: theme.palette.text.primary,
          mb: 2.5,
        }}
      >
        {/* The space between the spans is a real text node, not just the margin, so the
            heading reads and copies as "1. Who We Are" rather than "1.Who We Are". */}
        <Box component="span" sx={{ color: legalAccent(theme), mr: 0.5 }}>
          {number}.
        </Box>{" "}
        <Box component="span" id={`${section.id}-heading`}>
          {section.title}
        </Box>
      </Typography>
      <Stack spacing={2.5}>
        {section.blocks.map((block, index) => (
          <LegalBlock key={index} block={block} />
        ))}
      </Stack>
    </Box>
  );
};

export default LegalBlock;
